import { createContext, useContext, ReactNode } from 'react';
import { useVisitDay, DayStatus, ActivityType, TimeEntry, ClosedDriveData } from '../hooks/useVisitDay';
import { useOdometerContext } from './OdometerContext';

// Re-export types for consumers
export type { TimeEntry, ClosedDriveData };

interface Location {
  lat: number;
  lng: number;
  timestamp: Date;
  accuracy?: number;  // GPS accuracy in meters
}

interface DayData {
  status: DayStatus;
  startTime: Date | null;
  endTime: Date | null;
  startLocation: Location | null;
  currentLocation: Location | null;
  currentActivity: ActivityType;
  currentStoreId: number | null;
  currentProjectId: number | null;  // For store disambiguation when navigating back
  timeEntries: TimeEntry[];
  totalDriveMinutes: number;
  totalStoreMinutes: number;
  totalMiles: number;
}

interface DayTrackingContextType {
  dayData: DayData;
  /** The day's clock-in/out windows, oldest first (open window has no endTime). */
  clockWindows: TimeEntry[];
  isLoading: boolean;
  error: string | null;
  /** Fix a punch after the fact. `at: null` on 'out' reopens the window. */
  deleteLeg: (legId: string) => { ok: true } | { ok: false; reason: string };
  correctClockPunch: (legId: string, which: 'in' | 'out', at: Date | null) => void;
  /** Enter a window the rep never punched. */
  addClockWindow: (start: Date, end: Date | null) => void;
  /** Open a clock-in window. Several per calendar day are expected (#3452). */
  clockIn: (retroactiveStartTime?: Date, options?: { openDrive?: boolean }) => Promise<void>;
  /** Close the open clock-in window. The rep can clock in again afterwards. */
  clockOut: () => Promise<void>;
  startDay: (retroactiveStartTime?: Date, options?: { openDrive?: boolean }) => Promise<void>;
  endDay: () => Promise<void>;
  startDriving: () => Promise<void>;
  stopDriving: () => Promise<ClosedDriveData | undefined>;
  checkIntoStore: (storeId: number, storeName: string, projectId?: number) => Promise<ClosedDriveData | undefined>;
  checkOutOfStore: () => Promise<void>;
  cancelStoreVisit: (storeId: number) => void;
  resetDay: () => void;
  updateTimeEntry: (entryId: string, updates: { startTime?: Date; endTime?: Date; storeName?: string; startOdometer?: number; endOdometer?: number }) => void;
  addManualEntry: (type: 'drive' | 'store', startTime: Date, endTime: Date, storeName?: string, storeId?: number, startOdometer?: number, endOdometer?: number) => void;
  injectManualVisit: (visit: {
    storeId: number;
    storeName: string;
    projectId?: number;
    arrivedAt: Date;
    departedAt?: Date | null;
    driveStartOdometer?: number;
    driveEndOdometer?: number;
  }) => Promise<void>;
  applyVisitTimeCorrection: (
    callLogId: number,
    times: { startTime?: Date; endTime?: Date; driveStartTime?: Date; driveEndTime?: Date },
  ) => void;
  deleteTimeEntry: (entryId: string) => void;
  getElapsedMinutes: () => number;
  formatTime: (minutes: number) => string;
  setCurrentEntryCallLogId: (callLogId: number) => void;
  getLastCompletedStoreEntry: () => TimeEntry | undefined;
  markCallLogsVoided: (callLogIds: number[]) => void;
}

const DayTrackingContext = createContext<DayTrackingContextType | null>(null);

export function DayTrackingProvider({ children }: { children: ReactNode }) {
  // The rep's running odometer, so each drive leg records where it started and
  // ended without asking for another reading (#3452).
  const { odometerData } = useOdometerContext();
  const dayTracking = useVisitDay({ currentOdometer: odometerData.currentReading });

  return (
    <DayTrackingContext.Provider value={dayTracking}>
      {children}
    </DayTrackingContext.Provider>
  );
}

export function useDayTrackingContext() {
  const context = useContext(DayTrackingContext);
  if (!context) {
    throw new Error('useDayTrackingContext must be used within a DayTrackingProvider');
  }
  return context;
}
